import React, { Fragment, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash';
import Button from 'reactstrap/lib/Button';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { InventoryEntities, InventoryModel } from 'client/data/models/inventory';
import { PageModel } from 'client/data/models/page';
import { DISPLAY_PRICE } from 'site-modules/shared/constants/allowed-inventory-request-params';
import { FACETS_CONFIG, SUBGROUP_KEYS } from 'site-modules/shared/constants/inventory/usurp-filters-config';
import { MENU_HASH } from 'site-modules/core-page/utils/constants';
import { SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { getFilter } from 'site-modules/shared/utils/inventory/search-filter';
import { getAppliedRadius } from 'site-modules/shared/utils/inventory/srp-utils';
import { addFiltersToQuery } from 'site-modules/core-page/utils/core-srp';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { UsurpTabsGroup } from 'site-modules/shared/components/inventory/usurp-tabs-group/usurp-tabs-group';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';
import { Link } from 'site-modules/shared/components/link/link';

export function CorePricingHistogramUI({
  className,
  inventoryData,
  selectedFacets,
  visitorLocation,
  pathname,
  setModelValue,
}) {
  const hasInventory = !!get(inventoryData, 'inventories.totalNumber');
  const facets = get(inventoryData, 'facets', []);

  const radius = useMemo(() => getAppliedRadius({ inventoryData, visitorLocation, selectedFacets }), [
    inventoryData,
    selectedFacets,
    visitorLocation,
  ]);

  const onUpdate = useCallback(
    (type, facet, isChecked) => {
      const updatedFilter = getFilter({
        type,
        facet,
        isChecked,
        currentFilter: selectedFacets,
        radius,
        facets,
      });

      setModelValue('searchResultsFilter', InventoryModel, updatedFilter);

      addFiltersToQuery(pathname, updatedFilter);
    },
    [facets, pathname, radius, selectedFacets, setModelValue]
  );

  if (!inventoryData) {
    return null;
  }

  if (!hasInventory) {
    return (
      <Fragment>
        <div className="heading-4 text-center mb-1">Not available nearby</div>
        <Button
          tag={Link}
          to={srpLinkBuilder(selectedFacets)}
          className="rounded-8 w-100 py-0_75 text-transform-none bg-white mb-1"
          size="lg"
          color="outline-blue-50"
          rel="nofollow"
        >
          See All for Sale
        </Button>
      </Fragment>
    );
  }

  const pricingFacets = facets
    .map(facet => ({ ...facet, config: { ...FACETS_CONFIG[facet.type], creativeId: 'pricing_histogram' } }))
    .filter(facet => facet?.config?.subgroup === SUBGROUP_KEYS.PRICE_PAYMENT_TABS_SUBGROUP);

  return (
    <Fragment>
      <h2 className="heading-4 mb-1">Price & Payment</h2>
      <div className={classnames('core-pricing-histogram', className)}>
        <div className="d-flex flex-column align-items-center rounded-8 bg-white px-1 py-1">
          <UsurpTabsGroup
            className="mb-1_5"
            facets={pricingFacets}
            selectedTab={DISPLAY_PRICE}
            onUpdate={onUpdate}
            facetsConfig={FACETS_CONFIG}
            searchResultsFound={get(inventoryData, 'inventories.totalNumber', 0)}
          />

          <Button
            tag={ScrollLink}
            to={MENU_HASH.INVENTORY}
            scrollConfig={{ offset: SCROLL_OFFSET }}
            color="blue-50"
            size="lg"
            className="rounded-8 fw-medium text-transform-none mx-auto px-1_5 py-0_5"
            data-tracking-id="see_vin_listings"
          >
            See listings
          </Button>
        </div>
      </div>
    </Fragment>
  );
}

CorePricingHistogramUI.propTypes = {
  setModelValue: PropTypes.func.isRequired,
  className: PropTypes.string,
  pathname: PropTypes.string,
  inventoryData: PropTypes.shape({
    inventories: PropTypes.shape({
      results: InventoryEntities.InventoriesVin,
      totalNumber: PropTypes.number,
      totalPages: PropTypes.number,
    }),
    facets: InventoryEntities.Facets,
    seoInfo: PropTypes.shape({}),
  }),
  selectedFacets: PropTypes.shape({}),
  visitorLocation: VisitorEntities.Location,
};

CorePricingHistogramUI.defaultProps = {
  className: null,
  pathname: null,
  inventoryData: null,
  selectedFacets: null,
  visitorLocation: null,
};

export const propsAreEqual = (prevProps, { inventoryData }) => !inventoryData;
export const CorePricingHistogram = connectToModel(React.memo(CorePricingHistogramUI, propsAreEqual), {
  visitorLocation: bindToPath('location', VisitorModel, null, false),
  pathname: bindToPath('location.pathname', PageModel),
  inventoryData: bindToPath('searchResults', InventoryModel, null, false),
  selectedFacets: bindToPath('searchResultsFilter', InventoryModel, null, false),
});
